import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetContractByIdQuery } from 'store/api/contracts';
import { ContractForm, ContractType } from 'types/contracts';
import { useClassifiers } from 'utils/useClassifiers';

import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { EntityType } from 'types/entity';
import { CONTRACT_FORM_CLASSIFIERS, contractDtoToForm } from './contractFormMapping';
import ContractPageForm from './ContractPageForm';

const ClassicContractPageEdit = () => {
    const { id: contractId } = useParams();
    const [initialData, setInitialData] = useState<ContractForm>();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: CONTRACT_FORM_CLASSIFIERS,
    });

    const { data: contract, isError, isLoading, refetch } = useGetContractByIdQuery(contractId ?? skipToken);

    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.CONTRACT,
        fields: contract?.customFields,
    });

    useEffect(() => {
        if (isLoadingCustomFieldData) {
            return;
        }
        setInitialData(
            contract
                ? contractDtoToForm(contract, { classifiers, customFieldValues: initialCustomFieldsObject })
                : undefined
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classifiers, contract, isLoadingCustomFieldData]);

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError) || isLoadingCustomFieldData) {
        return <Loader />;
    }

    // A client identification has no form of its own: its page edits it block by block.
    if (contract?.contractTypeCode === ContractType.CLIENT_IDENTIFICATION) {
        return <Navigate to={`/contract/${contractId}`} replace />;
    }

    return (
        <ContractPageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            refetch={refetch}
            entityCustomFields={customFields}
            isDeletable={contract?.isDeletable}
        />
    );
};

/** In the new design a contract is edited section by section on its page, so its edit URL opens the Contract section. */
const ContractPageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: contractId } = useParams();
    return isNew ? <Navigate to={`/contract/${contractId}?edit=contract`} replace /> : <ClassicContractPageEdit />;
};

export default ContractPageEdit;
